import { useState, useEffect, useLayoutEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LayoutDashboard, GitMerge, Building2, Briefcase, FileText,
  Database, LogOut, ChevronDown, Link2, Bell, Layers, Activity,
  Server, Settings, Globe, Languages, KeyRound, Brain,
  Trophy, Search, Eye, Zap, FileCode, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ── Page titles ──
const PAGE_TITLES = {
  '/admin/dashboard': 'Dashboard',
  '/admin/cups': 'Cups Manager',
  '/admin/entities': 'Entities',
  '/admin/bookmakers': 'Bookmakers',
  '/admin/mapping': 'Mapping Queue',
  '/admin/data-quality': 'Data Quality',
  '/admin/providers': 'Providers',
  '/admin/config': 'Configuration',
  '/admin/cache': 'Cache Tools',
  '/admin/seo': 'SEO Overview',
  '/admin/pages': 'Pages Manager',
  '/admin/keywords': 'Keywords',
  '/admin/prompts': 'AI Prompts',
  '/admin/language-settings': 'Language & Tone',
  '/admin/ai-jobs': 'AI Content Jobs',
  '/admin/seo/audit': 'Advanced SEO Audit',
  '/admin/seo/audit/overview': 'Advanced SEO Audit',
  '/admin/seo/audit/technical': 'SEO Technical Audit',
  '/admin/seo/audit/index-health': 'SEO Index Health',
  '/admin/seo/audit/crawl': 'SEO Crawl Analysis',
  '/admin/seo/audit/content-quality': 'SEO Content Quality',
  '/admin/seo/audit/link-graph': 'SEO Link Graph',
  '/admin/seo/audit/opportunities': 'SEO Opportunities',
  '/admin/seo/audit/war-room': 'SEO War Room',
  '/admin/seo/audit/reports': 'SEO Reports',
  '/admin/seo/audit/fix-center': 'SEO AutoFix',
  '/admin/seo/audit/tasks': 'SEO Tasks',
  '/admin/jobs': 'System Jobs',
  '/admin/jobs/ai': 'AI Content Jobs',
  '/admin/alerts': 'Alerts',
  '/admin/bot-monitor': 'Bot Monitor',
  '/admin/affiliate': 'Affiliate',
  '/admin/logs': 'Logs',
  '/admin/change-password': 'Change Password',
};

// ── Sidebar structure ──
const NAV_GROUPS = [
  {
    label: 'DATA & OPERATIONS',
    icon: Database,
    items: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/admin/cups', label: 'Cups', icon: Trophy },
      { path: '/admin/entities', label: 'Entities', icon: Layers },
      { path: '/admin/bookmakers', label: 'Bookmakers', icon: Building2 },
      { path: '/admin/mapping', label: 'Mapping Queue', icon: GitMerge },
      { path: '/admin/data-quality', label: 'Data Quality', icon: Activity },
      { path: '/admin/providers', label: 'Providers', icon: Server },
      { path: '/admin/config', label: 'Config', icon: Settings },
      { path: '/admin/cache', label: 'Cache', icon: Database },
    ],
  },
  {
    label: 'SEO & CONTENT',
    icon: Globe,
    items: [
      { path: '/admin/seo', label: 'Overview', icon: BarChart3, exact: true },
      { path: '/admin/pages', label: 'Pages', icon: FileText },
      { path: '/admin/keywords', label: 'Keywords', icon: Search },
      { path: '/admin/prompts', label: 'AI Prompts', icon: FileCode },
      { path: '/admin/language-settings', label: 'Language & Tone', icon: Languages },
      { path: '/admin/seo/audit', label: 'Advanced Audit', icon: Brain, special: true },
    ],
  },
  {
    label: 'OPERATIONS & MONITOR',
    icon: Eye,
    items: [
      { path: '/admin/jobs', label: 'Jobs', icon: Briefcase },
      { path: '/admin/alerts', label: 'Alerts', icon: Bell },
      { path: '/admin/bot-monitor', label: 'Bot Monitor', icon: Eye },
      { path: '/admin/affiliate', label: 'Affiliate', icon: Link2 },
      { path: '/admin/logs', label: 'Logs', icon: FileText },
    ],
  },
];

function SidebarGroup({ group, location, openGroups, toggleGroup }) {
  const isOpen = openGroups[group.label] !== false;
  const isGroupActive = group.items.some(
    item => item.exact
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );
  const GIcon = group.icon;

  return (
    <div className="mb-1">
      <button
        onClick={() => toggleGroup(group.label)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors ${
          isGroupActive ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300'
        }`}
      >
        <GIcon className="w-3 h-3" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5 ml-1">
          {group.items.map(item => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link key={item.path} to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                  isActive
                    ? item.special ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                      : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : item.special ? 'text-purple-400/60 hover:text-purple-300 hover:bg-purple-500/5 border border-transparent'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${item.special ? 'text-purple-400' : ''}`} />
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * AdminOutletGuard — prevents stale route content from ever being visible.
 * 
 * Mechanism: `displayPath` lags behind `location.pathname` by one render cycle.
 * During the first render after a route change, location.pathname !== displayPath,
 * so a skeleton is returned SYNCHRONOUSLY (before any paint).
 * The useEffect then updates displayPath, triggering a second render that shows <Outlet />.
 */
function AdminOutletGuard() {
  const location = useLocation();
  const [displayPath, setDisplayPath] = useState(location.pathname);

  useEffect(() => {
    setDisplayPath(location.pathname);
  }, [location.pathname]);

  // Path mismatch = route just changed → show skeleton, hide old content
  if (location.pathname !== displayPath) {
    return (
      <div className="space-y-4" data-testid="admin-route-skeleton">
        <Skeleton className="h-8 w-48 bg-slate-800" />
        <Skeleton className="h-4 w-80 bg-slate-800/60" />
        <div className="grid grid-cols-4 gap-3 mt-4">
          {[0,1,2,3].map(i => <Skeleton key={i} className="h-20 bg-slate-800 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return <Outlet />;
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (label) => {
    setOpenGroups(prev => ({ ...prev, [label]: prev[label] === false ? true : false }));
  };

  // P0-2: document.title — useLayoutEffect fires BEFORE paint
  useLayoutEffect(() => {
    const path = location.pathname;
    const title = PAGE_TITLES[path]
      || Object.entries(PAGE_TITLES).find(([p]) => path.startsWith(p + '/'))?.[1];
    document.title = title ? `${title} | ODDSIS Admin` : 'ODDSIS Admin';
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && !user) navigate('/admin/login');
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-layout">
      <aside className="w-52 shrink-0 bg-slate-900 border-r border-slate-800 hidden md:flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <Link to="/" className="flex items-center">
            <img src="/oddsis-logo-dark.png" alt="ODDSIS" style={{ height: '32px', width: 'auto' }} />
          </Link>
        </div>

        <nav className="flex-1 p-2 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
          {NAV_GROUPS.map(group => (
            <SidebarGroup key={group.label} group={group} location={location} openGroups={openGroups} toggleGroup={toggleGroup} />
          ))}
        </nav>

        <div className="p-2 border-t border-slate-800">
          <div className="text-[10px] text-slate-600 mb-1 px-3">{user.username}</div>
          <Link to="/admin/change-password"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-amber-400 hover:bg-slate-800/50 transition-colors">
            <KeyRound className="w-3.5 h-3.5" />Change Password
          </Link>
          <Button variant="ghost" size="sm"
            onClick={() => { logout(); navigate('/admin/login'); }}
            className="w-full justify-start text-slate-400 hover:text-red-400 mt-0.5"
            data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <AdminOutletGuard />
        </div>
      </main>
    </div>
  );
}
